// The facility lists' filter mechanics (Tiendas, Observaciones): the state
// that lives in the URL, client-side filtering with per-chip counts, and
// paging. Pure and React-free.
//
// A facility-own copy of the mechanics in lib/postventa/observacionesList.ts
// (founder decision 2026-10-23: the postventa module stays untouched),
// adapted: fixed page size (no `?tam=`), no `?f=` group alias, and the
// filter keys / domains / row values come from each list's own module
// (tiendasList.ts, observacionesList.ts).

import { normalizeSearch } from "../util";

export const PAGE_SIZE = 25;

/** Query keys earlier versions of these screens wrote. Read never; any
 *  write drops them, so a link copied after one interaction is canonical.
 *  `asset` is the Observaciones store alias, migrated to `tienda` first. */
export const LEGACY_KEYS = ["tiendas_page", "obs_page", "asset"] as const;

export type ListState<K extends string> = Record<K, string[]> & {
  q: string;
  pagina: number;
};

export type ListPatch<K extends string> = Partial<Record<K, string[]>> & {
  q?: string;
  pagina?: number;
};

/** What each list declares about its own columns. */
export type ListConfig<K extends string> = {
  keys: readonly K[];
  /** Fixed domains: a URL value outside it is dropped on read. */
  domains?: Partial<Record<K, readonly string[]>>;
  /** A legacy query key read as this filter only when the filter's own
   *  key is absent (Observaciones: `?asset=` → `tienda`). */
  aliases?: Partial<Record<K, string>>;
};

/** The keys any write drops: the shared legacy ones plus this list's
 *  aliases. */
function legacyKeys<K extends string>(config: ListConfig<K>): string[] {
  const aliases = Object.values(config.aliases ?? {}) as string[];
  return [...LEGACY_KEYS, ...aliases];
}

function uniq(xs: readonly string[]): string[] {
  return [...new Set(xs)];
}

/** Reads the list state from the URL. Tolerant: an out-of-domain value is
 *  dropped and an invalid page is 1. */
export function parseListState<K extends string>(
  sp: URLSearchParams,
  config: ListConfig<K>
): ListState<K> {
  const filters = {} as Record<K, string[]>;
  for (const k of config.keys) {
    const alias = config.aliases?.[k];
    const raw = sp.has(k) || !alias ? sp.getAll(k) : sp.getAll(alias);
    const vals = uniq(raw.filter((v) => v !== ""));
    const dom = config.domains?.[k];
    filters[k] = dom ? vals.filter((v) => dom.includes(v)) : vals;
  }
  const pagina = Number.parseInt(sp.get("pagina") ?? "", 10);
  return {
    ...filters,
    q: sp.get("q") ?? "",
    pagina: Number.isFinite(pagina) && pagina >= 1 ? pagina : 1,
  };
}

/** Writes a patch over the previous URL. Every key at its default is
 *  DELETED (empty arrays, q "", page 1), so the clean URL is the canonical
 *  one. Any change that is not page-only goes back to page 1. Legacy keys
 *  are dropped — an alias still in charge of its filter is migrated to the
 *  filter's own key first, so the scope it carried survives. */
export function writeListState<K extends string>(
  prev: URLSearchParams,
  patch: ListPatch<K>,
  config: ListConfig<K>
): URLSearchParams {
  const next = new URLSearchParams(prev);
  for (const k of config.keys) {
    const alias = config.aliases?.[k];
    if (alias && !next.has(k) && patch[k] === undefined) {
      for (const v of uniq(next.getAll(alias).filter((x) => x !== ""))) next.append(k, v);
    }
  }
  for (const key of legacyKeys(config)) next.delete(key);
  for (const k of config.keys) {
    const v = patch[k];
    if (v === undefined) continue;
    next.delete(k);
    for (const x of uniq(v)) next.append(k, x);
  }
  if (patch.q !== undefined) {
    if (patch.q === "") next.delete("q");
    else next.set("q", patch.q);
  }
  const touchesFilter = config.keys.some((k) => patch[k] !== undefined) || patch.q !== undefined;
  if (patch.pagina !== undefined) {
    if (patch.pagina <= 1) next.delete("pagina");
    else next.set("pagina", String(patch.pagina));
  } else if (touchesFilter) {
    next.delete("pagina");
  }
  return next;
}

/** The canonical paging URL: when the raw `?pagina=` does not match what
 *  is painted (`?pagina=abc`, an out-of-range page already clamped to
 *  `pagina`, repeated keys) or a legacy key is still there, returns the
 *  corrected URL for a `replace`; null when it is already canonical. */
export function normalizePagingParams<K extends string>(
  sp: URLSearchParams,
  pagina: number,
  config: ListConfig<K>
): URLSearchParams | null {
  const next = writeListState(sp, { pagina } as ListPatch<K>, config);
  const samePage = sp.getAll("pagina").length <= 1 && sp.get("pagina") === next.get("pagina");
  const hasLegacy = legacyKeys(config).some((k) => sp.has(k));
  return samePage && !hasLegacy ? null : next;
}

/** Empties every filter and the search. */
export function clearFilters<K extends string>(keys: readonly K[]): ListPatch<K> {
  const patch = { q: "" } as ListPatch<K>;
  for (const k of keys) (patch as Record<K, string[]>)[k] = [];
  return patch;
}

export function hasActiveFilters<K extends string>(s: ListState<K>, keys: readonly K[]): boolean {
  return s.q.trim() !== "" || keys.some((k) => s[k].length > 0);
}

/** A list row: whatever the list needs, plus its id and the normalized
 *  text the search walks. */
export type Row = { id: string; haystack: string };

export type ApplyOptions<K extends string> = {
  /** Leave this column out (the chip being counted). */
  skip?: K;
  /** Row ids exempt from `keepIgnores` (rows just acted on stay put). */
  keep?: ReadonlySet<string>;
  keepIgnores?: readonly K[];
};

/** Filters with the whole state except, optionally, one column. */
export function applyFilters<K extends string, F extends Row>(
  filas: readonly F[],
  s: ListState<K>,
  keys: readonly K[],
  valueOf: (f: F, k: K) => string,
  opts: ApplyOptions<K> = {}
): F[] {
  const ql = normalizeSearch(s.q.trim());
  const active = keys.filter((k) => k !== opts.skip && s[k].length > 0);
  return filas.filter((f) => {
    const kept = opts.keep?.has(f.id) === true;
    for (const k of active) {
      if (kept && opts.keepIgnores?.includes(k)) continue;
      if (!s[k].includes(valueOf(f, k))) return false;
    }
    return !ql || f.haystack.includes(ql);
  });
}

export type FilterOption = { value: string; label: string; count: number };

export type OptionsSpec = {
  /** Fixed domain: every option is listed, even at 0. Without it only
   *  the values present in the rows (minus "") are offered. */
  domain?: readonly string[];
  labelOf: (value: string) => string;
  /** Open domains only: values listed ahead of the label-sorted rest. */
  pinFirst?: readonly string[];
};

/** A chip's options with their count against the OTHER filters. A URL
 *  value that no row carries stays listed (and checked) so it can be
 *  removed. */
export function optionsFor<K extends string, F extends Row>(
  filas: readonly F[],
  s: ListState<K>,
  k: K,
  keys: readonly K[],
  valueOf: (f: F, k: K) => string,
  spec: OptionsSpec
): FilterOption[] {
  const counts = new Map<string, number>();
  for (const f of applyFilters(filas, s, keys, valueOf, { skip: k })) {
    const v = valueOf(f, k);
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  let values: string[];
  if (spec.domain) {
    values = [...spec.domain];
  } else {
    const pin = spec.pinFirst ?? [];
    const present = uniq(filas.map((f) => valueOf(f, k)).filter((v) => v !== ""));
    const rest = present
      .filter((v) => !pin.includes(v))
      .sort((a, b) => spec.labelOf(a).localeCompare(spec.labelOf(b), "es", { numeric: true }));
    values = [...pin.filter((v) => present.includes(v)), ...rest];
  }
  for (const v of s[k]) if (!values.includes(v)) values.push(v);
  return values.map((v) => ({ value: v, label: spec.labelOf(v), count: counts.get(v) ?? 0 }));
}

// ── Paging ───────────────────────────────────────────────────────────

export function totalPages(total: number, size: number = PAGE_SIZE): number {
  return Math.max(1, Math.ceil(total / size));
}

/** The effective page: the URL's, clamped to the pages that exist. */
export function clampPage(pagina: number, total: number, size: number = PAGE_SIZE): number {
  return Math.min(Math.max(1, pagina), totalPages(total, size));
}

export function pageSlice<T>(rows: readonly T[], pagina: number, size: number = PAGE_SIZE): T[] {
  const start = (pagina - 1) * size;
  return rows.slice(start, start + size);
}

/** «Sin resultados» / «1 resultado» / «N resultados». */
export function resultados(n: number): string {
  return n === 0 ? "Sin resultados" : n === 1 ? "1 resultado" : `${n} resultados`;
}
